const SITE_NAME = 'Bichitos.life';
const SITE_URL = 'https://bichitos.life';
const DEFAULT_ROBOTS = 'index,follow';
const JSON_LD_ELEMENT_ID = 'app-jsonld';

type JsonLdValue = Readonly<Record<string, unknown>> | ReadonlyArray<Readonly<Record<string, unknown>>>;

export interface SeoMetadata {
  title: string;
  description: string;
  path?: string;
  canonicalUrl?: string;
  robots?: string;
  ogType?: string;
  imageUrl?: string | null;
  imageAlt?: string | null;
  jsonLd?: JsonLdValue | null;
}

export function buildSiteUrl(path: string): string {
  return new URL(path, SITE_URL).toString();
}

export function createWebsiteJsonLd(): Readonly<Record<string, unknown>> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: 'es',
  };
}

export function applySeoMetadata(metadata: SeoMetadata): void {
  const title = normalizeTitle(metadata.title);
  const description = normalizeDescription(metadata.description);
  const canonicalUrl = resolveCanonicalUrl(metadata);
  const robots = metadata.robots?.trim() || DEFAULT_ROBOTS;
  const ogType = metadata.ogType?.trim() || 'website';
  const imageUrl = resolveImageUrl(metadata.imageUrl);
  const imageAlt = metadata.imageAlt?.trim() || null;

  document.title = title;

  setMetaTag('name', 'description', description);
  setMetaTag('name', 'robots', robots);
  setMetaTag('property', 'og:locale', 'es_ES');
  setMetaTag('property', 'og:site_name', SITE_NAME);
  setMetaTag('property', 'og:type', ogType);
  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:url', canonicalUrl);
  setMetaTag('name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary');
  setMetaTag('name', 'twitter:title', title);
  setMetaTag('name', 'twitter:description', description);

  setLinkTag('canonical', canonicalUrl);

  if (imageUrl) {
    setMetaTag('property', 'og:image', imageUrl);
    setMetaTag('name', 'twitter:image', imageUrl);

    if (imageAlt) {
      setMetaTag('property', 'og:image:alt', imageAlt);
      setMetaTag('name', 'twitter:image:alt', imageAlt);
    } else {
      removeMetaTag('property', 'og:image:alt');
      removeMetaTag('name', 'twitter:image:alt');
    }
  } else {
    removeMetaTag('property', 'og:image');
    removeMetaTag('property', 'og:image:alt');
    removeMetaTag('name', 'twitter:image');
    removeMetaTag('name', 'twitter:image:alt');
  }

  setJsonLd(metadata.jsonLd ?? createWebsiteJsonLd());
}

function normalizeTitle(value: string): string {
  const normalizedValue = collapseWhitespace(value);

  if (normalizedValue === '') {
    return SITE_NAME;
  }

  if (normalizedValue.includes(SITE_NAME)) {
    return normalizedValue;
  }

  return `${normalizedValue} | ${SITE_NAME}`;
}

function normalizeDescription(value: string): string {
  const normalizedValue = collapseWhitespace(value);

  if (normalizedValue === '') {
    return 'Archivo editorial de insectos, taxonomias navegables y fichas de especies en Bichitos.life.';
  }

  if (normalizedValue.length <= 160) {
    return normalizedValue;
  }

  const truncatedValue = normalizedValue.slice(0, 157);
  const lastBoundary = truncatedValue.lastIndexOf(' ');
  const safeValue = lastBoundary >= 120 ? truncatedValue.slice(0, lastBoundary) : truncatedValue;

  return `${safeValue.trimEnd()}...`;
}

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function resolveCanonicalUrl(metadata: SeoMetadata): string {
  if (metadata.canonicalUrl?.trim()) {
    return new URL(metadata.canonicalUrl, SITE_URL).toString();
  }

  if (metadata.path?.trim()) {
    return buildSiteUrl(metadata.path);
  }

  return SITE_URL;
}

function resolveImageUrl(imageUrl: string | null | undefined): string | null {
  if (!imageUrl || imageUrl.trim() === '') {
    return null;
  }

  return new URL(imageUrl, SITE_URL).toString();
}

function setMetaTag(attributeName: 'name' | 'property', attributeValue: string, content: string): void {
  const selector = `meta[${attributeName}="${attributeValue}"]`;
  const element = getOrCreateHeadElement<HTMLMetaElement>(selector, () => {
    const meta = document.createElement('meta');
    meta.setAttribute(attributeName, attributeValue);
    document.head.append(meta);
    return meta;
  });

  element.setAttribute('content', content);
}

function removeMetaTag(attributeName: 'name' | 'property', attributeValue: string): void {
  document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`)?.remove();
}

function setLinkTag(rel: string, href: string): void {
  const selector = `link[rel="${rel}"]`;
  const element = getOrCreateHeadElement<HTMLLinkElement>(selector, () => {
    const link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.append(link);
    return link;
  });

  element.setAttribute('href', href);
}

function setJsonLd(value: JsonLdValue): void {
  const element = getOrCreateHeadElement<HTMLScriptElement>(`script#${JSON_LD_ELEMENT_ID}`, () => {
    const script = document.createElement('script');
    script.id = JSON_LD_ELEMENT_ID;
    script.type = 'application/ld+json';
    document.head.append(script);
    return script;
  });

  element.textContent = JSON.stringify(value);
}

function getOrCreateHeadElement<TElement extends Element>(
  selector: string,
  createElement: () => TElement,
): TElement {
  const existingElement = document.head.querySelector<TElement>(selector);

  if (existingElement) {
    return existingElement;
  }

  return createElement();
}
